import React, { useState, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { format, isPast } from "date-fns";
import { ClipboardList, Check } from "lucide-react";

import openSocket from "../../services/socket-io";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

// Persists task reminders on screen (bell-style popover) once the backend's
// task scheduler has already alerted the user about them, so they don't
// disappear with the toast — they stay until marked as done.
const TaskRemindersPopover = () => {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const buttonRef = useRef();
  const panelRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    const loadPendingTasks = async () => {
      try {
        const { data } = await api.get("/contacts/tasks/pending");
        setTasks(data);
      } catch (err) {
        toastError(err);
      }
    };
    if (user?.id) loadPendingTasks();
  }, [user?.id]);

  useEffect(() => {
    const handler = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const socket = openSocket();

    const handleTaskReminder = (data) => {
      if (data.action === "reminder") {
        setTasks((prev) => {
          if (prev.some((t) => t.id === data.task.id)) return prev;
          return [...prev, data.task].sort(
            (a, b) => new Date(a.dueDate) - new Date(b.dueDate)
          );
        });
      }
    };

    socket.on("taskReminder", handleTaskReminder);

    return () => {
      socket.off("taskReminder", handleTaskReminder);
    };
  }, []);

  const handleComplete = async (task) => {
    try {
      await api.put(`/contacts/tasks/${task.id}`, { status: "completed" });
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
    } catch (err) {
      toastError(err);
    }
  };

  const handleGoToContact = (task) => {
    setIsOpen(false);
    if (task.contact?.id) history.push(`/contacts`);
  };

  if (!user?.id) return null;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen((v) => !v)}
        className="relative p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        aria-label="Recordatorios de tareas"
        title="Recordatorios de tareas"
      >
        <ClipboardList className="w-4 h-4" />
        {tasks.length > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
            {tasks.length > 9 ? "9+" : tasks.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          className="absolute right-0 top-full mt-1.5 w-80 max-h-[360px] overflow-y-auto bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-lg z-50 animate-slide-up"
        >
          <div className="px-3 py-2.5 border-b border-slate-100 dark:border-slate-800">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Recordatorios de tareas
            </p>
          </div>
          {tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-400 gap-2">
              <ClipboardList className="w-8 h-8 opacity-30" />
              <p className="text-sm">No tienes recordatorios pendientes</p>
            </div>
          ) : (
            tasks.map((task) => {
              const overdue = task.dueDate && isPast(new Date(task.dueDate));
              return (
                <div
                  key={task.id}
                  className="flex items-start gap-2 px-3 py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-b-0 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                >
                  <button
                    onClick={() => handleGoToContact(task)}
                    className="flex-1 text-left min-w-0"
                  >
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                      {task.title}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {task.contact?.name || "Sin contacto"}
                    </p>
                    {task.dueDate && (
                      <p className={`text-[11px] mt-0.5 ${overdue ? "text-red-500" : "text-slate-400"}`}>
                        {format(new Date(task.dueDate), "dd/MM/yyyy HH:mm")}
                      </p>
                    )}
                  </button>
                  <button
                    onClick={() => handleComplete(task)}
                    title="Marcar como lista"
                    className="flex-shrink-0 p-1.5 rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default TaskRemindersPopover;
